import { Injectable, Signal, computed, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoService } from '@jsverse/transloco';
import { TranslocoLocaleService } from '@jsverse/transloco-locale';

export type SupportedLang = 'en' | 'de' | 'it';

const LANG_STORAGE_KEY = 'mahlzeit-lang';
const LANG_TO_LOCALE: Record<SupportedLang, string> = {
  en: 'en-US',
  de: 'de-DE',
  it: 'it-IT',
};

@Injectable({ providedIn: 'root' })
export class LocalizationService {
  private readonly _transloco = inject(TranslocoService);
  private readonly _translocoLocale = inject(TranslocoLocaleService);

  private readonly _lang = signal<SupportedLang>(this._readStoredLang());
  private readonly _translationsLoaded = signal(0);

  readonly activeLanguage: Signal<SupportedLang> = this._lang.asReadonly();
  readonly activeLocale: Signal<string> = computed(() => LANG_TO_LOCALE[this._lang()]);

  readonly translationsVersion: Signal<number> = this._translationsLoaded.asReadonly();

  constructor() {
    const lang = this._lang();
    this._transloco.setActiveLang(lang);
    this._translocoLocale.setLocale(LANG_TO_LOCALE[lang]);

    this._transloco.events$
      .pipe(takeUntilDestroyed())
      .subscribe(event => {
        if (event.type === 'translationLoadSuccess') {
          this._translationsLoaded.update(v => v + 1);
        }
      });
  }

  setLanguage(lang: SupportedLang): void {
    if (lang === this._lang()) return;
    this._lang.set(lang);
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    this._transloco.setActiveLang(lang);
    this._translocoLocale.setLocale(LANG_TO_LOCALE[lang]);
    this._translationsLoaded.update(v => v + 1);
  }

  private _readStoredLang(): SupportedLang {
    const stored = localStorage.getItem(LANG_STORAGE_KEY);
    if (stored === 'en' || stored === 'de' || stored === 'it') return stored;
    const nav = navigator.language.slice(0, 2);
    if (nav === 'en' || nav === 'de' || nav === 'it') return nav as SupportedLang;
    return 'en';
  }
}
